<script setup>

import StaticWidget from "@/components/common/other/StaticWidget.vue";
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown.vue";
import TextInput from "@/components/common/input/TextInput.vue";
import DateInput from "@/components/common/input/DateInput.vue";
import DimensionsEntry from "@/components/common/other/DimensionsEntry.vue";
import Button from "@/components/common/input/Button";
import TextArea from "@/components/common/input/TextArea";
import PillStyleSelection from "@/components/common/input/PillStyleSelection";
import NumberOnlyInput from "@/components/common/input/NumberOnlyInput.vue";
import Table from "@/components/common/other/Table";

import {computed, inject, ref} from "vue";
import API from "@/classes/global/API";
import {useRoute, useRouter} from "vue-router";
import {useGetChargeableWeight} from "@/composables/bookings/useGetChargeableWeight";
import store from "@/store";
import Notification from "@/classes/component-classes/Notification";
import SlideInDrawer from "@/components/common/navigation/SlideInDrawer.vue";
import DocumentUpload from "@/components/common/other/DocumentUpload.vue";
import {useGetValidatedFields} from "@/composables/common/useGetValidatedFields";
import {useValidateRequiredFields} from "@/composables/common/useValidateRequiredFields";
import QuoteModel from "@/classes/quote/QuoteModel";
import Tabs from "@/components/common/navigation/tabs/Tabs.vue";
import ShipmentDocumentDownload from "@/components/shipments/ShipmentDocumentDownload.vue";
import TabsContent from "@/components/common/navigation/tabs/TabsContent.vue";

const route = useRoute();
const router = useRouter();

const quote = inject("quote");
const validationViewModel = inject("validationViewModel");
const resetDims = ref(false);

const selectedDocsTab = ref("Current Docs");

const props = defineProps({

  modelValue: {}
})

function getChargeableWeightCalculation(totalWeight) {

  props.modelValue.unitOfMeasureSwitch ? props.modelValue.model.dimensions.unitOfMeasure = 'imperial' :
      props.modelValue.model.dimensions.unitOfMeasure = 'metric';

  const weightTotal = totalWeight.value; // copy computed value

  if(props.modelValue.model.transportMode === null || props.modelValue.model.transportMode === '') {
    props.modelValue.model.transportMode = 'other';
  }

  useGetChargeableWeight(route, weightTotal, props.modelValue.model, props.modelValue.model.transportMode);

}

async function setPortInformation(port, type) {

  if(type === 'origin') props.modelValue.model.origin = port.value.split('-')[1].trim();
  if(type === 'originPort') {

    props.modelValue.model.originPort = port.value.split('-')[0].trim();

    let result = await API.getPortCountry(port.id)

    props.modelValue.portSelections.originCountry = result;

  }

  if(type === 'destination') props.modelValue.model.destination = port.value.split('-')[1].trim();
  if(type === 'destinationPort') {

    props.modelValue.model.destinationPort = port.value.split('-')[0].trim();

    let result = await API.getPortCountry(port.id);

    props.modelValue.portSelections.destinationCountry = result;

  }

}

function selectTerm(term) {

  if(props.modelValue.quoteTerms[term.toLowerCase()] === true) {

    props.modelValue.model.freightTerms = '';

    return props.modelValue.quoteTerms[term.toLowerCase()] = false;
  }

  for(const each in props.modelValue.quoteTerms) {

     props.modelValue.quoteTerms[each] = false;
  }

  props.modelValue.quoteTerms[term.toLowerCase()] = true;
  props.modelValue.model.freightTerms = term;

}

function handleCurrencySelect(currency) {

  props.modelValue.model.valueCurrency = currency.id;
}

async function saveQuote() {

  let valid = useValidateRequiredFields(props.modelValue.model,
      validationViewModel.value.quote, props.modelValue.validatedFields);

  if(valid) {

    let response = await API.saveQuote(props.modelValue.model)

    if(response) {

      props.modelValue.model.dimensions.multiLineDims = response.dimensions.multiLineDims;
      props.modelValue.model.quoteRef = response.quoteRef;

      store.state.banners.success = new Notification(true, 'Quote Saved Successfully');

    }
  }
}

async function updateQuote() {

  let response = await API.updateQuote(props.modelValue.model)

  if(response) {

    props.modelValue.model.dimensions.multiLineDims = response.dimensions.multiLineDims;
    store.state.banners.success = new Notification(true, 'Quote Updated Successfully');
  }
}

function handleNewQuote() {

  router.push({ name: 'quote', params: { id: 'new' }});

  props.modelValue.model = new QuoteModel();

  resetDims.value = !resetDims.value;

  for(let each in props.modelValue.portSelections) {

    props.modelValue.portSelections[each] = '';
  }

  for(let each in props.modelValue.quoteTerms) {

    props.modelValue.quoteTerms[each] = false;
  }

}

function handleClearPort(model)
{
  props.modelValue.model[model[0]] = '';
  props.modelValue.portSelections[model[1]] = '';
}

async function sendDocumentUpdate() {

  let response = await API.updateQuoteDocuments(props.modelValue.model.quoteRef, props.modelValue.model.files);

  if(response) store.state.banners.success = new Notification(true, 'Quote Docs Updated Successfully');

}

async function handleDocuments(){

  props.modelValue.openDocsDrawer = true;

  let result = await API.getQuoteDocuments(props.modelValue.model.quoteRef);

  if(result) props.modelValue.shipmentDocs = result;

}

async function handleAcceptQuote() {

  props.modelValue.model.charges = props.modelValue.chargeDescriptionsData
  props.modelValue.model.totalCharges =  props.modelValue.chargesTotal;

  let response = await API.acceptQuoteNotification(props.modelValue.model, props.modelValue.model.quoteRef);

  if(response) store.state.banners.success = new Notification(true, 'Quote Acceptance Sent Successfully');
}


</script>

<template>
  <SlideInDrawer
      :height="370"
      title="Upload Documents"
      v-model:open-drawer="modelValue.openDocsDrawer">
    <Tabs v-model:selectedTab="selectedDocsTab">
      <TabsContent
          name="Current Docs"
          :selectedTab="selectedDocsTab">
        <ShipmentDocumentDownload
            v-model="modelValue">
        </ShipmentDocumentDownload>
      </TabsContent>
      <TabsContent
          name="Add Docs"
          :selectedTab="selectedDocsTab">
        <DocumentUpload v-model="modelValue.model"></DocumentUpload>
        <Button
            style="position: absolute; right: 50px; bottom: 70px"
            @click="sendDocumentUpdate()"
            text="Send Notification">
        </Button>
      </TabsContent>
    </Tabs>
  </SlideInDrawer>
  <div class="cmp-quote-entry_wrapper">
    <StaticWidget title="Quote Details" height="725" background="#fbfbfb">
      <template #right-side>
        <Button
            @click="handleDocuments"
            type="warning"
            text="Documents">
        </Button>
        <Button
            v-if="!modelValue.model.quoteRef"
            text="Save & Submit"
            @click="saveQuote">
        </Button>
        <Button
            v-else
            text="Update"
            type="success"
            :disabled="modelValue.jobAccepted"
            @click="updateQuote">
        </Button>
      </template>
      <template #extra-content>
        <Button
            @click="handleNewQuote"
            type="toolbar"
            text="New Quote">
        </Button>
        <div v-if="modelValue.model.quoteRef" style="margin-left: 150px; font-weight: bold; color: #484848">Quote Reference:
          <span style="margin-left: 10px; font-weight: normal; font-size: 14px">{{modelValue.model.quoteRef}}</span>
        </div>
      </template>
      <div class="cmp-quote-entry_container">
        <div class="cmp-quote-entry_top-section">
          <div class="global-card global-center-items" style="height: 40px">
            <MultiSearchDropDown
                :disable="modelValue.jobAccepted"
                :width="310"
                :label-width="80"
                label="Quote Type"
                style="padding-left: 20px"
                placeholder="Select..."
                :allowCustomValue="true"
                :disableFilter="true"
                :required-field="useGetValidatedFields(modelValue.validatedFields, 'quoteType')"
                v-model:validation-fail="validationViewModel.quote.quoteType"
                v-model="modelValue.model.quoteType"
                :data-source="modelValue.quoteTypes"
                :selectPrimitiveValue="true">
            </MultiSearchDropDown>
          </div>
          <div class="global-card global-center-items" style="height: 40px;">
            <MultiSearchDropDown
                :disable="modelValue.jobAccepted"
                :width="300"
                :label-width="40"
                style="padding-left: 24px"
                label="Mode"
                placeholder="Select..."
                :required-field="useGetValidatedFields(modelValue.validatedFields, 'transportMode')"
                v-model:validation-fail="validationViewModel.quote.transportMode"
                :allowCustomValue="false"
                :disableFilter="true"
                v-model="modelValue.model.transportMode"
                :data-source="modelValue.modes"
                :selectPrimitiveValue="true">
            </MultiSearchDropDown>
          </div>
        </div>
        <div class="cmp-quote-entry_top-section">
          <div class="global-card" style="height: 170px;">
            <div class="cmp-quote-entry_card-title global-center-items">
              <img src="@/assets/icons/address-marker.png">Origin
            </div>
            <div class="global-css_vertical-form-layout" style=" padding-left: 20px; margin-top: 5px">
              <MultiSearchDropDown
                  :disable="modelValue.jobAccepted"
                  :key="modelValue.model.origin"
                  :width="320"
                  :maxLength="50"
                  :label-width="70"
                  @select="setPortInformation($event, 'origin')"
                  @clear="handleClearPort('origin')"
                  :required-field="useGetValidatedFields(modelValue.validatedFields, 'origin')"
                  v-model:validation-fail="validationViewModel.quote.origin"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  searchType="iata"
                  v-model="modelValue.portSelections.originSelection"
                  :transport-mode="modelValue.model.transportMode || 'all'"
                  label="Origin"
                  placeholder="Search Code....">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :disable="modelValue.jobAccepted"
                  :key="modelValue.model.originPort"
                  :width="320"
                  :maxLength="60"
                  :label-width="70"
                  @select="setPortInformation($event, 'originPort')"
                  @clear="handleClearPort(['originPort', 'originCountry'])"
                  :allowCustomValue="true"
                  :selectPrimitiveValue="true"
                  :required-field="useGetValidatedFields(modelValue.validatedFields, 'originPort')"
                  v-model:validation-fail="validationViewModel.quote.originPort"
                  :useServerSearch="true"
                  searchType="iata"
                  v-model="modelValue.portSelections.originPort"
                  :transport-mode="modelValue.model.transportMode || 'all'"
                  label="Origin Port"
                  placeholder="Search Code....">
              </MultiSearchDropDown>
              <TextInput
                  label="Port Country"
                  :width="320"
                  :label-width="70"
                  :disable="true"
                  v-model="modelValue.portSelections.originCountry">
              </TextInput>
              <DateInput
                  :required-field="useGetValidatedFields(modelValue.validatedFields, 'movementDate')"
                  v-model:validation-fail="validationViewModel.quote.movementDate"
                  :disable="modelValue.jobAccepted"
                  :width="120"
                  :label-width="70"
                  v-model="modelValue.model.movementDate"
                  label="Collect By">
                <el-date-picker
                    v-model="modelValue.model.movementDate"
                    :clearable="false"
                    :disabled="modelValue.jobAccepted"
                    value-format="DD-MMM-YY"/>
              </DateInput>
            </div>
          </div>
          <div class="global-card" style="height: 170px;">
            <div class="cmp-quote-entry_card-title global-center-items">
              <img src="@/assets/icons/destination.png" height="22" width="22">
              <span style="margin-left: 10px">Destination</span>
            </div>
            <div class="global-css_vertical-form-layout" style=" padding-left: 20px; margin-top: 12px">
              <MultiSearchDropDown
                  :disable="modelValue.jobAccepted"
                  :key="modelValue.portSelections.destinationSelection"
                  :width="320"
                  :maxLength="50"
                  :label-width="100"
                  @select="setPortInformation($event, 'destination')"
                  @clear="handleClearPort('destination')"
                  :required-field="useGetValidatedFields(modelValue.validatedFields, 'destination')"
                  v-model:validation-fail="validationViewModel.quote.destination"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  v-model="modelValue.portSelections.destinationSelection"
                  :useServerSearch="true"
                  searchType="iata"
                  :transport-mode="modelValue.model.transportMode || 'all'"
                  label="Destination"
                  placeholder="Search Code....">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  :disable="modelValue.jobAccepted"
                  :key="modelValue.portSelections.destinationPort"
                  :width="320"
                  :maxLength="50"
                  :label-width="100"
                  :required-field="useGetValidatedFields(modelValue.validatedFields, 'destinationPort')"
                  v-model:validation-fail="validationViewModel.quote.destinationPort"
                  @select="setPortInformation($event, 'destinationPort')"
                  @clear="handleClearPort(['destinationPort', 'destinationCountry'])"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  :useServerSearch="true"
                  searchType="iata"
                  v-model="modelValue.portSelections.destinationPort"
                  :transport-mode="modelValue.model.transportMode || 'all'"
                  label="Destination Port"
                  placeholder="Search Code....">
              </MultiSearchDropDown>
              <TextInput
                  label="Port Country"
                  :width="320"
                  :label-width="100"
                  :disable="true"
                  v-model="modelValue.portSelections.destinationCountry">
              </TextInput>
              <DateInput
                  :required-field="useGetValidatedFields(modelValue.validatedFields, 'deliveryDate')"
                  v-model:validation-fail="validationViewModel.quote.deliveryDate"
                  :disable="modelValue.jobAccepted"
                  :width="120"
                  :label-width="100"
                  v-model="modelValue.model.deliveryDate"
                  label="Deliver By">
                <el-date-picker
                    v-model="modelValue.model.deliveryDate"
                    :clearable="false"
                    :disabled="modelValue.jobAccepted"
                    value-format="DD-MMM-YY"/>
              </DateInput>
            </div>
          </div>
        </div>
        <div class="global-card" style="height: 150px; width: auto; background: #fffcf8" >
          <div class="cmp-quote-entry_card-title global-center-items">
            <img src="@/assets/icons/consignment.png" height="28" width="28">
            <span style="margin-left: 10px">Consignment Details</span>
          </div>
          <div class="cmp-quote-entry_consignment-details">
            <div class="global-css_vertical-form-layout">
              <TextInput
                  :required-field="useGetValidatedFields(modelValue.validatedFields, 'customerRef')"
                  v-model:validation-fail="validationViewModel.quote.customerRef"
                  :disable="modelValue.jobAccepted"
                  :label-width="105"
                  :width="506"
                  v-model="modelValue.model.customerRef"
                  label="Customer Ref">
            </TextInput>
              <div :class="{'global-required-field' : useGetValidatedFields(modelValue.validatedFields, 'commodity')}"
                   style="position: absolute; top: 90px; font-size: 12px">Goods Description</div>
              <TextArea
                  v-model:validation-fail="validationViewModel.quote.commodity"
                  :readonly="quote.jobAccepted"
                  v-model="modelValue.model.commodity"
                  @input="modelValue.model.commodity = $event"
                  style="margin-left: 110px"
                  :width="500">
              </TextArea>
            </div>
            <div>
              <div class="global-flex-layout">
                <MultiSearchDropDown
                    :disable="modelValue.jobAccepted"
                    :key="store.state.metaData.currencies"
                    :width="180"
                    :label-width="70"
                    label="Currency"
                    :required-field="useGetValidatedFields(modelValue.validatedFields, 'valueCurrency')"
                    v-model:validation-fail="validationViewModel.quote.origin"
                    @select="handleCurrencySelect"
                    style="padding-left: 20px"
                    placeholder="Select..."
                    :allowCustomValue="true"
                    :disableFilter="false"
                    v-model="modelValue.portSelections.currencySelect"
                    :data-source="store.state.metaData.currencies"
                    :selectPrimitiveValue="false">
                </MultiSearchDropDown>
                <NumberOnlyInput
                    :disable="modelValue.jobAccepted"
                    v-model="modelValue.model.value"
                    :required-field="useGetValidatedFields(modelValue.validatedFields, 'value')"
                    v-model:validation-fail="validationViewModel.quote.value"
                    style="margin-left: 10px"
                    :label-width="43"
                    :allow-decimal="true"
                    :width="70"
                    label="Value">
                </NumberOnlyInput>
              </div>
              <MultiSearchDropDown
                  :disable="modelValue.jobAccepted"
                  :key="modelValue.hazardClasses"
                  v-model="modelValue.model.hazardClass"
                  :width="320"
                  style="margin-top: 5px"
                  :required-field="useGetValidatedFields(modelValue.validatedFields, 'hazardClass')"
                  v-model:validation-fail="validationViewModel.quote.hazardClass"
                  :maxLength="50"
                  :label-width="90"
                  label="Hazard Class"
                  placeholder="Select..."
                  :allowCustomValue="true"
                  :disableFilter="true"
                  :data-source="modelValue.hazardClasses"
                  :selectPrimitiveValue="true">
              </MultiSearchDropDown>
              <div style=" margin: 14px 0 0 310px">
                <PillStyleSelection
                    @click="modelValue.model.insurance = !modelValue.model.insurance"
                    v-model="modelValue.model.insurance">
                  <template #content>
                    <div style="font-size: 11px">Insurance</div>
                  </template>
                </PillStyleSelection>
              </div>
            </div>
          </div>
        </div>
        <div class="cmp-quote-entry_terms-container">
          <div class="cmp-quote-entry_card-title">Terms Of Shipment</div>
          <div class="cmp-quote-entry_terms-selection">
            <PillStyleSelection
                @click="selectTerm('EXW')"
                v-model="modelValue.quoteTerms.exw">
              <template #content>
                <div style="font-weight:bold; font-size: 11px">EXW -
                <span style="font-size: 10px; font-weight: normal">Ex Works</span>
                </div>
              </template>
            </PillStyleSelection>
            <PillStyleSelection
                @click="selectTerm('CIP')"
                v-model="modelValue.quoteTerms.cip">
              <template #content>
                <div style="font-weight:bold; font-size: 11px">CIP -
                  <span style="font-size: 10px; font-weight: normal">Carriage & Insurance Paid To</span>
                </div>
              </template>
            </PillStyleSelection>

            <PillStyleSelection
                @click="selectTerm('CPT')"
                v-model="modelValue.quoteTerms.cpt">
              <template #content>
                <div style="font-weight:bold; font-size: 11px">CPT -
                  <span style="font-size: 10px; font-weight: normal">Carriage Paid To</span>
                </div>
              </template>
            </PillStyleSelection>
            <PillStyleSelection
                @click="selectTerm('DAP')"
                v-model="modelValue.quoteTerms.dap">
              <template #content>
                <div style="font-weight:bold; font-size: 11px">DAP -
                  <span style="font-size: 10px; font-weight: normal">Delivered At Place</span>
                </div>
              </template>
            </PillStyleSelection>
          </div>
          <div class="cmp-quote-entry_terms-selection">
            <PillStyleSelection
                @click="selectTerm('FCA')"
                v-model="modelValue.quoteTerms.fca">
              <template #content>
                <div style="font-weight:bold; font-size: 11px">FCA -
                  <span style="font-size: 10px; font-weight: normal">Free Carrier</span>
                </div>
              </template>
            </PillStyleSelection>

            <PillStyleSelection
                @click="selectTerm('DAT')"
                v-model="modelValue.quoteTerms.dat">
              <template #content>
                <div style="font-weight:bold; font-size: 11px">DAT -
                  <span style="font-size: 10px; font-weight: normal">Delivered At Terminal</span>
                </div>
              </template>
            </PillStyleSelection>
            <PillStyleSelection
                @click="selectTerm('DDP')"
                v-model="modelValue.quoteTerms.ddp">
              <template #content>
                <div style="font-weight:bold; font-size: 11px">DDP -
                  <span style="font-size: 10px; font-weight: normal">Delivered Duty Paid</span>
                </div>
              </template>
            </PillStyleSelection>
          </div>
        </div>
        <div class="global-card" style="height: 240px; width: auto; background: #fffcfc">
          <div class="cmp-quote-entry_dims-section">
            <div>
              <div style="display: flex; align-items: center; margin: 0 0 20px -30px">
               <img src="@/assets/icons/package_dims.png" style="margin-right: 10px" height="26" width="26" />
                <div class="label" style="display: flex; align-items: center;">Packaging Details
                  <span class="cmp-quote-entry_dims-message">
                  Pieces, Length, Width & Height are required to add a line of dims
                </span>
                </div>
                <div class="label" style="display: flex; margin-left: 20px; align-items: center;">
                  <span style="font-weight: normal;">Unit Of Measure</span>
                  <span style="margin-left: 30px;">
                      <el-switch
                          v-model="modelValue.model.unitOfMeasureSwitch"
                          size="small"
                          active-text="lbs / inches"
                          inactive-text="kg / cms"
                          style="--el-switch-on-color:var(--aqua-blue-definition); --el-switch-off-color: var(--aqua-blue-definition)"/>
                  </span>
                </div>
              </div>
              <DimensionsEntry
                  :key="resetDims"
                  :readOnly="modelValue.jobAccepted"
                  @entry-update="getChargeableWeightCalculation"
                  :packaging="store.state.metaData.packagingTypes"
                  :requiredFields="props.modelValue.validatedFields"
                  v-model="modelValue.model.dimensions.multiLineDims">
              </DimensionsEntry>
            </div>
            <div class="cmp-quote-entry_dims-totals">
              <div style="font-size: 12px; font-weight: bold; margin-left: 10px">Totals
                <span style="font-weight: normal; font-size: 10px; font-style: italic">You may enter pieces and weight
                </span>
              </div>
              <div class="global-flex-layout" style="padding: 30px 0 20px 10px; gap:100px">
                <NumberOnlyInput
                    :disable="modelValue.jobAccepted"
                    v-model="modelValue.model.dimensions.totalPieces"
                    :width="60"
                    :label-top="true"
                    label="Pieces">
                </NumberOnlyInput>
                <NumberOnlyInput
                    :disable="modelValue.jobAccepted"
                    v-model="modelValue.model.dimensions.totalWeight"
                    :width="90"
                    :label-top="true"
                    label="Weight">
                </NumberOnlyInput>
              </div>
              <div class="global-flex-layout" style="padding: 20px 0 0 10px; gap:40px">
                <NumberOnlyInput
                    v-model="modelValue.model.dimensions.chargeableWeight"
                    :width="120"
                    :readonly="true"
                    :label-top="true"
                    label="Chargeable Weight">
                </NumberOnlyInput>
                <NumberOnlyInput
                    v-model="modelValue.model.dimensions.cube"
                    :width="90"
                    :label-top="true"
                    :readonly="true"
                    label="Cube">
                </NumberOnlyInput>
              </div>
            </div>
          </div>
        </div>
        <div v-if="modelValue.chargeDescriptionsData.length > 0" class="global-card" style="height: 200px; width: auto; background: #f9fcff">
          <div class="cmp-quote-entry_charge-descriptions-header">
            <img src="@/assets/icons/money_exchange.png" height="22" width="22">
            <div class="cmp-quote-entry_card-title">
              <div>Charge Descriptions</div>
            </div>
            <Button
                v-if="!modelValue.jobAccepted"
                style="margin-top: 20px"
                type="success"
                @click="handleAcceptQuote"
                text="Accept Quote">
            </Button>
          </div>
          <div class="cmp-quote-entry_charge-descriptions">
            <div class="cmp-quote-entry_charge-descriptions-total">Total Charges:
              <div>{{modelValue.chargesTotal}}</div>
            </div>
            <Table
                height="90"
                :allowGridMenu="false"
                :selectable="false"
                :row-selection="false"
                :columns="modelValue.chargeDescriptionsColumns"
                :dataSource="modelValue.chargeDescriptionsData">
            </Table>
          </div>
        </div>
        <div class ="cmp-quote-entry_terms-container">
          <div class="cmp-quote-entry_card-title">Comments</div>
          <TextArea
              @input="modelValue.model.additionalInformation = $event"
              v-model="modelValue.model.additionalInformation"
              style="margin-top: 10px"
              :width="1160">
        </TextArea>
        </div>
      </div>
    </StaticWidget>
  </div>
</template>

<style scoped lang="scss">

.cmp-quote-entry {

  &_wrapper {
    //width: 1180px;
    //margin: 0 auto;
  }

  &_container {
    padding: 10px 30px;
    width: 1180px;
    margin: 0 auto;
    height: 710px;
    overflow-y: scroll;
  }

  &_top-section {
    display: flex;
    gap: 140px;
  }

  &_card-title {
    font-size: 13px;
    font-weight: bold;
  }

  &_consignment-details {
    margin: 10px 20px 0 20px;
    display: flex;
    gap: 40px;
  }

  &_terms-container {
    padding: 10px 0 0 0;
  }

  &_terms-selection {
    margin-top: 14px;
    display: flex;
    gap: 30px;
  }

  &_dims-section {
    margin: 10px 0 0 30px;
    display: flex;
    gap:30px;
  }

  &_dims-totals {
    padding: 10px;
    border-radius: var(--rounded-corners);
    background-color: #f2f8fc;
    height: 140px;
    width: 280px;
  }

  &_dims-message {
    font-weight: normal;
    background-color: #fffbf3;
    padding: 5px;
    font-style: italic;
    font-size: 11px;
    border: 1px solid #f1f1f1;
    border-radius: var(--rounded-corners);
    color: var(--aqua-blue-definition);
    margin-left: 10px;
  }

  &_charge-descriptions {
    padding: 10px 0 0 30px;
  }

  &_charge-descriptions-total {
    height: 20px;
    width: max-content;
    display: flex;
    border-radius: 6px;
    background: #ffe8e8;
    font-size: 13px;
    align-items: center;
    padding: 0 10px;
    gap: 10px;
    margin-bottom: 5px;
    border: 1px solid #ffcfcf;
  }

  &_charge-descriptions-header {
    display: grid;
    grid-template-columns: 30px 1000px auto;
    align-items: center;
  }
}

</style>